import { inject, type InjectionKey } from 'vue'

/**
 * ``inject`` wrapper that logs a dev-only warning when the expected provider
 * is missing and falls back to a caller-supplied default value.
 *
 * Silent ``inject(key, fallback)`` call sites mask integration bugs (test
 * harnesses forgetting to ``provide()``, component refactors dropping a
 * provider, Storybook mounts, etc.). This helper surfaces those cases in
 * development while keeping production noiseless and keeping the fallback
 * semantics intact.
 *
 * :param key: Injection key (string or typed :class:`InjectionKey`).
 * :param fallback: Value returned when the provider is absent.
 * :param componentName: Optional component name used in the warning prefix so
 *     the console message points straight at the consumer.
 *
 * :return: The injected value when a provider exists, otherwise ``fallback``.
 */
export function injectWithWarn<T>(
  key: string | InjectionKey<T>,
  fallback: T,
  componentName?: string
): T {
  const injected = inject<T | undefined>(key as string | InjectionKey<T>, undefined)
  if (injected !== undefined) return injected
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    const scope = componentName ? `[${componentName}]` : '[injectWithWarn]'
    console.warn(`${scope} Missing "${String(key)}" provider — using fallback.`)
  }
  return fallback
}
